import React, { useMemo, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, TableSortLabel,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import EditIcon from "@mui/icons-material/Edit";

// Opaco (no un tono semi-transparente): con stickyHeader, un fondo
// transparente deja ver las filas de abajo a través del header al hacer scroll.
const Th = styled(TableCell)(({ theme }) => ({
  fontWeight: 700,
  fontSize: "0.78rem",
  padding: "6px 8px",
  whiteSpace: "nowrap",
  color: theme.palette.mode === "dark" ? theme.palette.common.white : theme.palette.primary.dark,
  backgroundColor: theme.palette.mode === "dark" ? theme.palette.grey[800] : "#eaf1fb",
  "& .MuiTableSortLabel-icon": {
    color: "inherit !important",
  },
}));

const COLUMNAS = [
  { field: "fecha", label: "Fecha", numeric: false },
  { field: "descripcion", label: "Descripción", numeric: false },
  { field: "adicional", label: "Adicional", numeric: false },
  { field: "cod_transaccion", label: "Código de transacción", numeric: false },
  { field: "credito", label: "Crédito", numeric: true },
  { field: "debito", label: "Débito", numeric: true },
  { field: "banco", label: "Banco", numeric: false },
  { field: "observacion", label: "Observación", numeric: false },
  { field: "saldo", label: "Saldo", numeric: true },
];

const formatMonto = (value) => {
  const n = Number(value);
  return n ? n.toFixed(2) : "0.00";
};

const ExtractoHistoricoTable = ({ rows, filterText, onEdit }) => {
  const [openCodDialog, setOpenCodDialog] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [orderBy, setOrderBy] = useState("fecha");
  const [order, setOrder] = useState("asc");

  const handleOpenCodDialog = (row) => {
    setSelectedId(row.id);
    setOpenCodDialog(true);
  };

  const handleCloseCodDialog = () => {
    setOpenCodDialog(false);
    setSelectedId(null);
  };

  const handleSort = (field) => {
    if (orderBy === field) {
      setOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setOrderBy(field);
      setOrder("asc");
    }
  };

  const filteredRows = rows.filter((row) =>
    Object.values(row).some((value) =>
      String(value).toLowerCase().includes(filterText.toLowerCase())
    )
  );

  const sortedRows = useMemo(() => {
    const columna = COLUMNAS.find((c) => c.field === orderBy);
    const factor = order === "asc" ? 1 : -1;
    return [...filteredRows].sort((a, b) => {
      if (columna?.numeric) {
        return (Number(a[orderBy]) - Number(b[orderBy])) * factor;
      }
      const va = String(a[orderBy] ?? "").toLowerCase();
      const vb = String(b[orderBy] ?? "").toLowerCase();
      return va.localeCompare(vb) * factor;
    });
  }, [filteredRows, orderBy, order]);

  return (
    <>
      <TableContainer>
        <Table size="small" sx={{ "& td": { py: 0.5, px: 1, fontSize: "0.78rem" } }}>
          <TableHead>
            <TableRow>
              {COLUMNAS.map((c) => (
                <Th key={c.field} align={c.numeric ? "right" : "left"} sortDirection={orderBy === c.field ? order : false}>
                  <TableSortLabel
                    active={orderBy === c.field}
                    direction={orderBy === c.field ? order : "asc"}
                    onClick={() => handleSort(c.field)}
                  >
                    {c.label}
                  </TableSortLabel>
                </Th>
              ))}
              <Th align="center">Acciones</Th>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMNAS.length + 1}>Sin resultados.</TableCell>
              </TableRow>
            ) : sortedRows.map((r, i) => (
              <TableRow key={r.id ?? i} hover>
                <TableCell>{r.fecha ? String(r.fecha).slice(0, 10) : "-"}</TableCell>
                <TableCell>{r.descripcion || "-"}</TableCell>
                <TableCell>{r.adicional || "-"}</TableCell>
                <TableCell>
                  <span
                    style={{ cursor: "pointer", color: "#1976d2" }}
                    onClick={() => handleOpenCodDialog(r)}
                  >
                    {r.cod_transaccion || "-"}
                  </span>
                </TableCell>
                <TableCell align="right">{formatMonto(r.credito)}</TableCell>
                <TableCell align="right">{formatMonto(r.debito)}</TableCell>
                <TableCell>{r.banco || "-"}</TableCell>
                <TableCell>{r.observacion || "-"}</TableCell>
                <TableCell align="right">{formatMonto(r.saldo)}</TableCell>
                <TableCell align="center">
                  <EditIcon
                    titleAccess="Editar"
                    onClick={() => onEdit(r)}
                    sx={{
                      fontSize: 16,
                      cursor: "pointer",
                      color: "action.active",
                      "&:hover": { color: "primary.main" },
                    }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openCodDialog} onClose={handleCloseCodDialog}>
        <DialogTitle>Código de transacción</DialogTitle>
        <DialogContent>
          <Typography>ID: {selectedId}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCodDialog}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ExtractoHistoricoTable;
